import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Search, Plus, ChevronDown, ChevronRight } from '../../icons';
import { ATT_HOME_PATH, attMenuFor, filterAttMenu } from '../../lib/att-menu';
// 2열 묶음 배치 · 지금 위치를 화면 안에 (2026-10-17 · web 레인 W5 — lib/menu-layout.ts 머리말)
import { ATT_LAYOUT, regroup } from '../../lib/menu-layout';
import { useActiveIntoView } from '../../hooks/useActiveIntoView';
// 신청서 목록은 **한 곳**에 있다 — 양식이 늘면 그 파일만 고친다.
import { HR_REQUEST_FORMS, FORM_GROUP_AT, SUBSTITUTE_FORM, approvalDraftPath } from './my-attendance-links';
// 휴일대체(보상휴가 §57) — 후보를 서버가 줘야 하는 신청이라 **팝업**으로 연다(정본 P3-3).
import { SubstituteRequestModal } from './SubstituteRequestModal';
import { getSubstitutePolicy } from '../../api/hr-substitute';
import { useAnchoredMenu } from '../../hooks/useAnchoredMenu';
import { useIsAdminViewer } from '../../lib/menu-catalog';
import { fetchAttendanceToday } from '../../api/hr-attendance-today';
// 「내가 무엇을 맡았나」는 한 번만 묻는다 — 화면 둘이 같은 답을 쓴다(403 을 만들지 않게).
import { getMyDutyScope } from '../../lib/my-org-scope';
import { listWorkSchedules } from '../../api/hr-schedule';
import './AttShell.css';

/**
 * 근태 셸 — 좌 240px 트리 + 우 화면(`<Outlet/>`).
 *
 * ── 무엇이 바뀌나 (2026-08-29 · 판 A) ────────────────────────────────────
 * 근태 화면 셋은 **이미 있었다**. 바뀌는 것은 그 셋이 **트리를 인 채로 뜬다**는 것뿐이고,
 * **주소는 한 자도 안 바뀐다** — `router/index.tsx` 에서 같은 경로가 부모 `erp/attendance` +
 * 자식(index · `codes` · `team`)으로 나뉘었을 뿐이다. 자식의 `element` 도 그대로다.
 *
 * ── 왜 가드를 두지 않나 ──────────────────────────────────────────────────
 * 인사 셸과 같은 판단이다. 담당자 화면은 저마다 제 가드를 갖고 있고(팀 근태는 **서버가**
 * 판정한다 — 팀장은 역할이 STAFF 라 `RoleGuard` 로 못 가린다), 셸에 가드를 두면
 * **직원이 「내 근태」에도 못 들어온다.** 프론트 가드는 UX 게이트이고 권위 있는 차단은 서버가 한다.
 *
 * ── 생김새 ───────────────────────────────────────────────────────────────
 * 인사 셸(`.hrnav`)과 같은 규칙·같은 치수(240px)지만 **클래스 이름은 따로 둔다**(`attnav-*`).
 * 같은 이름을 두 도메인이 나눠 쓰면 한쪽을 고칠 때 다른 쪽이 함께 움직인다 —
 * `hrnav-*` 를 `cadm-*` 와 갈라 둔 것과 같은 사정이다.
 */
/**
 * 팝업 크기 — `useAnchoredMenu` 가 **열기 전에** 넘침을 판정하려면 알아야 한다(CSS 와 함께 고친다).
 * 폭은 버튼에 맞추므로(`matchAnchorWidth`) 높이만 실제 값에 가깝게 둔다.
 */
const FORMS_MENU = { width: 240, height: 300 };


export function AttShell() {
  const { pathname } = useLocation();
  const isAdmin = useIsAdminViewer();
  const [query, setQuery] = useState('');
  /**
   * 맡은 조직이 있는가 — 팀장에게 「근태 현황」을 세우는 조건(2026-08-30).
   * ★ 팀장은 역할이 STAFF 라 **역할로는 가릴 수 없다** — 판정은 서버(`orgScope`)가 한다.
   *   못 읽으면 거짓: **모르면 안 보여 주는 쪽**이다.
   */
  const [hasOrg, setHasOrg] = useState(false);
  /**
   * ★★★ **전사 근태를 볼 수 있나** (2026-08-31) — 「근태 관리」를 세우는 두 번째 근거.
   *
   *    근태 담당자는 **역할이 `STAFF`** 다(담당자 부여, 근태 C1). 그래서 `useIsAdminViewer()`
   *    로는 직원과 구별되지 않아, **부여를 해 줘도 트리에 아무것도 안 섰다** — 갈 길이 없었다.
   *    이제 서버가 `/users/me` 에서 한 칸으로 답한다(찔러 보지 않는다 — 403 을 만들지 않게).
   * ★ 못 읽으면 거짓: **모르면 안 보여 주는 쪽**(옛 서버는 그 칸을 안 준다).
   */
  const [canSeeCompany, setCanSeeCompany] = useState(false);
  /**
   * 트리 배지 — **열지 않아도 어긋난 것이 몇인지 보인다** (2026-08-30 · 판 F).
   * ★ 「이상 근태」의 총계와 **같은 셈**이다(오늘 미출근 + 어제 이전 퇴근 누락) — 트리와 화면이
   *   다른 수를 말하면 둘 다 못 믿게 된다.
   * ★ 못 받으면(전사를 못 보는 사람 403 · 옛 서버 404) **숫자를 안 그린다** — 0 으로 그리면
   *   「어긋난 것이 없다」는 **거짓말**이 된다.
   */
  const [anomalies, setAnomalies] = useState<number | null>(null);
  /**
   * ★★ 이 회사가 **교대를 쓰는가** (2026-08-30 · 판 K). 새 스위치를 만들지 않고,
   *   `SHIFT` 근무제가 한 줄이라도 있으면 「교대 편성」이 트리에 선다 — **조를 만든 것이 곧
   *   「우리는 교대를 쓴다」** 는 뜻이다. 못 읽으면 거짓(모르면 안 보여 주는 쪽).
   */
  const [hasShift, setHasShift] = useState(false);
  /**
   * ★ **전사를 보는 사람** = 관리자 **또는** 부여받은 사람. 한 낱말로 묶어 두는 것은,
   *   아래 효과가 이 값 하나에만 매달리게 하려는 것이다 — 관리자에게는 처음부터 참이라
   *   `canSeeCompany` 가 뒤늦게 참이 돼도 **다시 부르지 않는다**(같은 요청을 두 번 안 낸다).
   */
  const seesCompany = isAdmin || canSeeCompany;

  /**
   * ── 신청서 작성 · 근무 상태 (2026-09-06 · 사용자 지시) ────────────────
   *
   * ★★★ 둘 다 **버튼 아래에 뜨는 팝업**이다. 판 A 는 제자리 펼침이었는데, 그러면
   *    ① 목록이 길어질수록 아래 트리가 통째로 밀려 내려가고
   *    ② 「상태」가 **칩 여섯**으로 늘 펼쳐져 있어 레일이 그 사람의 오늘로 꽉 찬다.
   * ★★★ **잘림은 「팝업이라서」가 아니라 「상자 안에 그려서」 생긴다** — 판 A 는 그 관찰에서
   *    「팝오버를 쓰지 말자」로 갔지만, 이 저장소에는 이미 **버튼 자리를 재서 body 에
   *    `position:fixed` 로 띄우는** 길이 있다(`useAnchoredMenu`). 그러면 레일 밖으로 나간다.
   *    스크롤 · 창 크기 변경 · Esc 에는 닫힌다(좌표가 어긋난 채 떠 있는 것보다 낫다).
   * ★ 펼침을 기기에 남기던 것은 **없앴다** — 팝업은 «열어 두는 것»이 아니라 «집었다 놓는 것»이다.
   */
  const forms = useAnchoredMenu<HTMLButtonElement>(FORMS_MENU, { align: 'start', matchAnchorWidth: true });

  /**
   * ── 휴일대체를 쓰는 회사인가 (2026-09-06 · 판 B) ─────────────────────
   *
   * ★★ **모르면 안 세운다.** §57 은 회사가 「근로자대표와의 서면 합의」로 켜는 제도라 기본이
   *    꺼져 있고, 안 켠 회사에 줄을 세우면 눌러도 「이 회사는 안 씁니다」밖에 못 말한다 —
   *    **할 수 없는 일을 메뉴에 세우면 그게 잘못된 안내다.**
   * ★ 옛 서버에는 이 라우트가 없다(404) — 그때도 안 세운다(조용히 없다).
   * ★ 이것만은 **직원에게도 묻는다** — 신청하는 사람이 직원이라 담당자 조건으로 가릴 수 없다.
   */
  const [substituteOn, setSubstituteOn] = useState(false);
  const [substituteOpen, setSubstituteOpen] = useState(false);

  const menu = useMemo(
    () => regroup(attMenuFor(isAdmin, hasOrg, hasShift, canSeeCompany), ATT_LAYOUT),
    [isAdmin, hasOrg, hasShift, canSeeCompany],
  );
  const groups = useMemo(() => filterAttMenu(menu, query), [menu, query]);
  useActiveIntoView('.attnav', pathname);
  /**
   * ★★★ **휴일대체는 «넷째»로 끼운다** — 자주 내는 셋(선 위) 바로 다음, 곧 선 아래 첫 자리다.
   *    회사가 켠 곳에서만 선다. 나머지 여섯의 자리·차례는 한 칸도 안 움직인다.
   */
  const formItems = useMemo(
    () => (substituteOn
      ? [...HR_REQUEST_FORMS.slice(0, FORM_GROUP_AT), SUBSTITUTE_FORM, ...HR_REQUEST_FORMS.slice(FORM_GROUP_AT)]
      : HR_REQUEST_FORMS),
    [substituteOn],
  );
  const hit = groups.some((g) => g.items.length > 0);

  // 휴일대체를 켠 회사인가 — 실패(404 옛 서버 · 네트워크)면 «꺼져 있다»로 둔다.
  useEffect(() => {
    let alive = true;
    getSubstitutePolicy()
      .then((pol) => { if (alive) setSubstituteOn(pol.enabled === true); })
      .catch(() => { if (alive) setSubstituteOn(false); });
    return () => { alive = false; };
  }, []);

  // 「내가 무엇을 맡았나 · 무엇을 볼 수 있나」 — **한 번만** 묻는다(요청도 하나다).
  useEffect(() => {
    let alive = true;
    void getMyDutyScope().then((s) => {
      if (!alive) return;
      setHasOrg(s.orgScope !== 'NONE');
      setCanSeeCompany(s.canSeeCompanyAttendance);
    });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    /**
     * ★★★ **직원에게는 부르지 않는다** (2026-08-31) — 이 숫자는 **「이상 근태」 배지**에만 쓰이고
     *    그 항목은 담당자 묶음(`ADMIN_GROUP`)에만 선다. 그런데 종전에는 **누구에게나** 불러
     *    직원이 근태를 열 때마다 `company-today` 가 **403** 을 남겼다.
     *    바로 아래 근무제 목록에 적어 둔 규칙이 여기 안 와 있었을 뿐이다.
     */
    // ★ 부여받은 사람도 이 숫자를 본다 — 「이상 근태」가 그 사람의 트리에도 서기 때문이다.
    if (seesCompany) {
      void (async () => {
        try {
          const t = await fetchAttendanceToday();
          if (alive) setAnomalies(t.absent + t.openRecords);
        } catch {
          if (alive) setAnomalies(null);
        }
      })();
    }
    // 근무제 목록은 **담당자만** 볼 수 있다 — 직원에게는 부르지 않는다(403 을 만들지 않게).
    if (isAdmin) {
      void (async () => {
        try {
          const res = await listWorkSchedules();
          if (alive) setHasShift(res.schedules.some((s) => s.type === 'SHIFT' && s.isActive !== false));
        } catch {
          if (alive) setHasShift(false);
        }
      })();
    }
    return () => { alive = false; };
  }, [isAdmin, seesCompany]);

  /** 「내 근태」(뿌리)만 정확히 일치로 켠다 — 아니면 하위 화면에서 둘이 함께 켜진다. */
  const isOn = (to: string) =>
    to === ATT_HOME_PATH ? pathname === ATT_HOME_PATH : pathname === to || pathname.startsWith(`${to}/`);

  return (
    <div className="attshell">
      <nav className="attnav" aria-label="근태 메뉴">
        <div className="attnav__head">
          <h2 className="attnav__title">근태</h2>
          <div className="attnav__search">
            <Search size={16} aria-hidden />
            <input
              className="attnav__searchinput"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="근태 메뉴 검색"
              aria-label="근태 메뉴 검색"
            />
          </div>
        </div>

        {/*
          ★★ 신청서 작성 — 누르면 **아래로** 펼친다. 양식 목록은 `my-attendance-links.ts` 한 곳이고,
             화면이 목록을 따로 갖지 않는다(양식이 늘면 그 파일만 고친다).
        */}
        <div className="attnav__ctawrap">
          <button
            type="button"
            className="attnav__cta"
            ref={forms.anchorRef}
            onClick={forms.toggle}
            aria-expanded={forms.open}
            aria-haspopup="menu"
            aria-controls="attnav-forms"
          >
            <span className="attnav__ctai"><Plus size={15} aria-hidden />신청서 작성</span>
            {forms.open ? <ChevronDown size={15} aria-hidden /> : <ChevronRight size={15} aria-hidden />}
          </button>
        </div>
        {/*
          ★ 팝업은 **레일 밖**(body)에 그린다 — 레일이 `overflow-y:auto` 라 안에 그리면 잘린다.
          ★ 가려진 곳을 눌러도 닫히게 **투명한 덮개**를 함께 깐다(스크롤·Esc 는 훅이 닫는다).
        */}
        {forms.pos && createPortal(
          <>
            <div className="attnav__scrim" onClick={forms.close} aria-hidden />
            <div
              className="attnav__pop"
              id="attnav-forms"
              role="menu"
              aria-label="신청서 작성"
              style={{ top: forms.pos.top, left: forms.pos.left, width: forms.pos.anchorWidth }}
            >
              {formItems.map((f, i) => {
                const cls = `attnav__form${i === FORM_GROUP_AT ? ' is-sep' : ''}`;
                /*
                  ★★★ **일곱째는 링크가 아니라 단추다** (2026-09-06 · 판 B · 정본 P3-3).
                     양식만으로는 못 내는 신청이라 — 「어느 날 근로를 바꿀 수 있는가」를
                     서버가 알려 줘야 한다 — 기안 화면 대신 후보 목록을 연다.
                */
                if (f.code === SUBSTITUTE_FORM.code) {
                  return (
                    <button
                      key={f.code}
                      type="button"
                      role="menuitem"
                      className={cls}
                      onClick={() => { forms.close(); setSubstituteOpen(true); }}
                    >
                      <f.icon size={14} aria-hidden />{f.label}
                    </button>
                  );
                }
                return (
                  <Link
                    key={f.code}
                    to={approvalDraftPath(f.code)}
                    role="menuitem"
                    className={cls}
                    onClick={forms.close}
                  >
                    <f.icon size={14} aria-hidden />{f.label}
                  </Link>
                );
              })}
            </div>
          </>,
          document.body,
        )}

        {/*
          ★★★ **「오늘」(출근 · 퇴근 · 근무 상태)은 걷어냈다** (2026-09-08 사용자 지시).
             2026-09-06 에 「내 근태」 본문에서 이 레일로 올렸던 것인데, 다시 뺀다.
          ★★ **찍을 곳이 사라지지 않는다** — 출근 · 퇴근 · 근무 상태는 **상태바의 「상태」
             드롭다운 · 프로필 팝오버 · 대시보드 위젯**에 그대로 있다(걷어내기 전에 확인했다).
             레일은 다시 «어디로 갈지»와 «무엇을 낼지»만 갖는다.
        */}

        {groups.map((g) => (
          <div className="attnav__group" key={g.label}>
            <div className="attnav__glabel">{g.label}</div>
            {g.items.map((i) => {
              const Icon = i.icon;
              return (
                <Link
                  key={i.to}
                  to={i.to}
                  className={`attnav__item${isOn(i.to) ? ' is-on' : ''}`}
                  aria-current={isOn(i.to) ? 'page' : undefined}
                >
                  <Icon size={17} aria-hidden />
                  <span className="attnav__nm">
                    {i.label}
                    {/* ★ 배지는 «몇 건인가» 다 — 열지 않아도 손볼 것이 있는지 보인다. */}
                    {i.to === '/erp/attendance/anomalies' && anomalies !== null && anomalies > 0 && (
                      <span className="attnav__badge" aria-label={`${anomalies}건`}>{anomalies}</span>
                    )}
                  </span>
                  <span className="attnav__hint">{i.hint}</span>
                </Link>
              );
            })}
          </div>
        ))}
        {!hit && <p className="attnav__none">찾는 메뉴가 없습니다.</p>}
      </nav>

      <div className="attnav__body">
        <Outlet />
      </div>

      {/* ★ 셸이 들고 있어야 «화면을 옮겨도» 신청 중이던 것이 안 사라진다(레일과 같은 자리). */}
      {substituteOpen && <SubstituteRequestModal onClose={() => setSubstituteOpen(false)} />}
    </div>
  );
}
